import { MapPin, Mail, Fingerprint, ExternalLink, ArrowUpRight } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";
import { Reveal, Overline } from "./Reveal";

export const ContactSection = () => {
  const { t } = useLang();
  return (
    <section id="contact" data-testid="contact-section" className="bg-white py-28 lg:py-36">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <Overline>{t.contact.overline}</Overline>
          <h2 className="mt-6 font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">
            {t.contact.title}
          </h2>
          <p className="mt-4 text-base md:text-lg text-slate-600">{t.contact.lead}</p>
        </Reveal>

        <div className="mt-16 grid md:grid-cols-3 gap-px bg-slate-200 border border-slate-200">
          <Reveal className="h-full">
            <div data-testid="contact-address-card" className="bg-white h-full p-10">
              <MapPin size={20} strokeWidth={1.6} className="text-emerald-700" />
              <h3 className="mt-5 text-lg font-medium text-slate-900">{t.contact.addressTitle}</h3>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed whitespace-pre-line">{t.contact.address}</p>
            </div>
          </Reveal>

          <Reveal delay={0.08} className="h-full">
            <div data-testid="contact-client-card" className="bg-[#FAF9F6] h-full p-10 flex flex-col">
              <ArrowUpRight size={20} strokeWidth={1.6} className="text-emerald-700" />
              <h3 className="mt-5 text-lg font-medium text-slate-900">{t.contact.clientTitle}</h3>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">{t.contact.clientNote}</p>
              <a
                data-testid="contact-client-area-link"
                href="https://client.asteo.eu/"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-6 inline-flex items-center gap-2 self-start px-5 py-2.5 bg-slate-900 text-white text-sm rounded-full hover:bg-emerald-800 transition-colors duration-300"
              >
                {t.contact.clientCta} <ArrowUpRight size={14} />
              </a>
              <span className="mt-4 text-xs font-mono text-slate-500">client.asteo.eu</span>
            </div>
          </Reveal>

          <Reveal delay={0.16} className="h-full">
            <div data-testid="contact-lei-card" className="bg-white h-full p-10">
              <Fingerprint size={20} strokeWidth={1.6} className="text-emerald-700" />
              <h3 className="mt-5 font-mono text-sm tracking-wide text-slate-900">{t.contact.lei}</h3>
              <a
                data-testid="contact-gleif-link"
                href="https://search.gleif.org/#/record/222100L7BX1XCBJHH958"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-2 text-sm text-emerald-700 hover:text-emerald-900 transition-colors duration-300"
              >
                {t.contact.leiNote} <ExternalLink size={13} />
              </a>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-px border border-t-0 border-slate-200 bg-white p-10" data-testid="contact-departments">
            <div className="flex items-center gap-3">
              <Mail size={18} strokeWidth={1.6} className="text-emerald-700" />
              <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">{t.contact.deptsTitle}</span>
            </div>
            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-7">
              {t.contact.depts.map((d, i) => (
                <div key={i} className="group">
                  <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-slate-500">{d.name}</span>
                  <a
                    data-testid={`contact-email-${i}`}
                    href={`mailto:${d.email}`}
                    className="mt-1 block text-sm text-slate-900 hover:text-emerald-700 transition-colors duration-300"
                  >
                    {d.email}
                  </a>
                  <span className="block mt-2 h-px w-0 bg-emerald-700 group-hover:w-10 transition-all duration-500" />
                </div>
              ))}
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
